// ---------------------------------------------------------------------------
// System Sync Health Page — Phase 5: Cross-tenant sync monitoring dashboard
// Shows per-tenant sync status pulled from system-api, styled to match SystemStatusPage
// ---------------------------------------------------------------------------

import React, { useState } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Clock,
  Database,
  HelpCircle,
  RefreshCw,
  Pause,
  Play,
  Search,
  WifiOff,
  XCircle,
  Zap,
} from 'lucide-react';
import { useTenantSyncPolling, type TenantSyncRecord } from '../services/healthPollingService';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function timeSince(iso: string): string {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function HealthDot({ status }: { status: TenantSyncRecord['healthStatus'] }) {
  const color =
    status === 'healthy'      ? '#16a34a' :
    status === 'degraded'     ? '#d97706' :
    status === 'error'        ? '#dc2626' :
    status === 'idle'         ? '#2563eb' :
    status === 'undetermined' ? '#9ca3af' :
    '#6b7280';
  return <span className="h-2 w-2 rounded-full flex-shrink-0 inline-block" style={{ background: color }} />;
}

function HealthBadge({ status }: { status: TenantSyncRecord['healthStatus'] }) {
  const map = {
    healthy:      { Icon: CheckCircle,   color: '#16a34a', bg: 'rgba(22,163,74,0.1)',    label: 'Healthy'      },
    degraded:     { Icon: AlertTriangle, color: '#d97706', bg: 'rgba(217,119,6,0.1)',    label: 'Degraded'     },
    error:        { Icon: XCircle,       color: '#dc2626', bg: 'rgba(220,38,38,0.1)',    label: 'Error'        },
    offline:      { Icon: WifiOff,       color: '#6b7280', bg: 'rgba(107,114,128,0.1)',  label: 'Offline'      },
    idle:         { Icon: Pause,         color: '#2563eb', bg: 'rgba(37,99,235,0.1)',    label: 'Idle'         },
    undetermined: { Icon: HelpCircle,    color: '#9ca3af', bg: 'rgba(156,163,175,0.1)',  label: 'Undetermined' },
  } as const;
  const { Icon, color, bg, label } = map[status];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ background: bg, color }}>
      <Icon className="h-3 w-3" />{label}
    </span>
  );
}

function StatCard({ label, value, sub, icon: Icon, accent }: {
  label: string; value: string; sub?: string; icon: React.ElementType; accent?: string;
}) {
  return (
    <div className="flex-1 min-w-0 rounded-lg p-3 border" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'rgba(0,0,0,0.02)' }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-3.5 w-3.5 opacity-50" />
        <span className="text-xs opacity-60 font-medium uppercase tracking-wide">{label}</span>
      </div>
      <p className="text-xl font-bold tabular-nums" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-xs opacity-50 mt-0.5">{sub}</p>}
    </div>
  );
}

function SectionCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border mb-4 overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)', background: 'var(--card-bg, white)' }}>
      {children}
    </div>
  );
}

function SectionHeader({ title, icon: Icon, expanded, onToggle, badge }: {
  title: string; icon: React.ElementType; expanded: boolean; onToggle: () => void; badge?: React.ReactNode;
}) {
  return (
    <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left"
      style={{ borderBottom: expanded ? '1px solid rgba(0,0,0,0.06)' : 'none' }}>
      <Icon className="h-4 w-4 opacity-60" />
      <span className="font-semibold text-sm flex-1">{title}</span>
      {badge}
      {expanded ? <ChevronUp className="h-4 w-4 opacity-40" /> : <ChevronDown className="h-4 w-4 opacity-40" />}
    </button>
  );
}

function TenantRow({ tenant }: { tenant: TenantSyncRecord }) {
  const [expanded, setExpanded] = useState(false);

  const hasIssues = tenant.failedPushCount > 0 || tenant.unresolvedConflictCount > 0 || tenant.healthStatus === 'error';

  return (
    <div className="border-b last:border-0" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
      {/* Summary row */}
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-black/[0.02] transition-colors"
      >
        <HealthDot status={tenant.healthStatus} />
        <span className="text-sm font-medium w-48 truncate">{tenant.tenantName}</span>
        <span className="text-xs opacity-40 font-mono w-28 truncate">{tenant.tenantSlug}</span>

        {/* Realtime indicator */}
        <span className="flex items-center gap-1 text-xs w-20"
          style={{ color: tenant.sseConnected ? '#16a34a' : '#6b7280' }}>
          <Zap className="h-3 w-3" />
          {tenant.sseConnected ? 'Live' : 'Offline'}
        </span>

        {/* Last sync */}
        <span className="text-xs opacity-50 w-28 truncate">
          {tenant.lastSuccessfulSyncAt ? timeSince(tenant.lastSuccessfulSyncAt) : 'Never'}
        </span>

        {/* Counts */}
        <span className="flex items-center gap-3 ml-auto text-xs">
          {tenant.pendingPushCount > 0 && (
            <span style={{ color: '#d97706' }}>↑ {tenant.pendingPushCount}</span>
          )}
          {tenant.failedPushCount > 0 && (
            <span style={{ color: '#dc2626' }}>✗ {tenant.failedPushCount}</span>
          )}
          {tenant.unresolvedConflictCount > 0 && (
            <span style={{ color: '#dc2626' }}>⚡ {tenant.unresolvedConflictCount}</span>
          )}
          {!hasIssues && <span className="opacity-30">—</span>}
        </span>

        {expanded ? <ChevronUp className="h-3.5 w-3.5 opacity-30 ml-2" /> : <ChevronDown className="h-3.5 w-3.5 opacity-30 ml-2" />}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-4 pb-4 pt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 bg-black/[0.01]">
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Health</p>
            <HealthBadge status={tenant.healthStatus} />
          </div>
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Collections synced</p>
            <p className="font-semibold">{tenant.activeCollections} / {tenant.totalCollections}</p>
          </div>
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Last successful sync</p>
            <p className="font-semibold">{tenant.lastSuccessfulSyncAt ? timeSince(tenant.lastSuccessfulSyncAt) : 'Never'}</p>
          </div>
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Pending upload</p>
            <p className="font-semibold" style={{ color: tenant.pendingPushCount > 0 ? '#d97706' : undefined }}>
              {tenant.pendingPushCount} item{tenant.pendingPushCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Failed pushes</p>
            <p className="font-semibold" style={{ color: tenant.failedPushCount > 0 ? '#dc2626' : undefined }}>
              {tenant.failedPushCount} item{tenant.failedPushCount !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="rounded-lg p-3 border text-sm" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
            <p className="text-xs opacity-50 mb-1">Unresolved conflicts</p>
            <p className="font-semibold" style={{ color: tenant.unresolvedConflictCount > 0 ? '#dc2626' : undefined }}>
              {tenant.unresolvedConflictCount}
            </p>
          </div>
          {tenant.errorMessage && (
            <div className="col-span-full rounded-lg p-3 border text-sm"
              style={{ borderColor: 'rgba(220,38,38,0.2)', background: 'rgba(220,38,38,0.04)' }}>
              <p className="text-xs font-semibold mb-1" style={{ color: '#991b1b' }}>Last error</p>
              <p className="text-xs opacity-70 break-words">{tenant.errorMessage}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function SyncHealthPage() {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'healthy' | 'degraded' | 'error' | 'offline' | 'idle' | 'undetermined'>('all');
  const [expanded, setExpanded] = useState({ tenants: true, summary: true });
  const {
    data: tenants,
    lastUpdated,
    loading,
    refresh,
  } = useTenantSyncPolling(autoRefresh);

  const toggle = (key: keyof typeof expanded) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const fetchData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    try {
      await refresh();
    } finally {
      if (isManual) setRefreshing(false);
    }
  };

  const filtered = tenants.filter(t => {
    if (filterStatus !== 'all' && t.healthStatus !== filterStatus) return false;
    if (search && !t.tenantName.toLowerCase().includes(search.toLowerCase()) &&
        !t.tenantSlug.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Aggregate stats
  const totalHealthy      = tenants.filter(t => t.healthStatus === 'healthy').length;
  const totalDegraded     = tenants.filter(t => t.healthStatus === 'degraded').length;
  const totalError        = tenants.filter(t => t.healthStatus === 'error').length;
  const totalOffline      = tenants.filter(t => t.healthStatus === 'offline').length;
  const totalIdle         = tenants.filter(t => t.healthStatus === 'idle').length;
  const totalUndetermined = tenants.filter(t => t.healthStatus === 'undetermined').length;
  const totalPending  = tenants.reduce((s, t) => s + t.pendingPushCount, 0);
  const totalFailed   = tenants.reduce((s, t) => s + t.failedPushCount, 0);
  const totalConflicts = tenants.reduce((s, t) => s + t.unresolvedConflictCount, 0);
  const totalLive     = tenants.filter(t => t.sseConnected).length;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <RefreshCw className="h-6 w-6 animate-spin opacity-40" />
        <span className="ml-3 opacity-60">Loading sync health data…</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Activity className="h-5 w-5" style={{ color: '#f59e0b' }} />
            Sync Health
          </h1>
          {lastUpdated && <p className="text-xs opacity-50 mt-0.5">Updated {timeSince(lastUpdated)}</p>}
        </div>
        <button
          onClick={() => setAutoRefresh(p => !p)}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)', color: autoRefresh ? '#16a34a' : undefined }}
          title={autoRefresh ? 'Auto-refresh on (30s)' : 'Auto-refresh paused'}
        >
          {autoRefresh ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          {autoRefresh ? '30s' : 'Paused'}
        </button>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm border transition-colors"
          style={{ borderColor: 'rgba(0,0,0,0.12)' }}
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard label="Total Tenants" value={String(tenants.length)} sub={`${totalLive} live realtime`} icon={Database} />
        <StatCard
          label="Healthy"
          value={String(totalHealthy)}
          sub={`${totalDegraded + totalError} issues`}
          icon={CheckCircle}
          accent={totalHealthy === tenants.length && tenants.length > 0 ? '#16a34a' : undefined}
        />
        <StatCard
          label="Pending Push"
          value={String(totalPending + totalFailed)}
          sub={totalFailed > 0 ? `${totalFailed} failed` : 'across all tenants'}
          icon={Activity}
          accent={totalFailed > 0 ? '#dc2626' : totalPending > 0 ? '#d97706' : undefined}
        />
        <StatCard
          label="Conflicts"
          value={String(totalConflicts)}
          sub="unresolved across all tenants"
          icon={AlertTriangle}
          accent={totalConflicts > 0 ? '#dc2626' : undefined}
        />
      </div>

      {/* Status breakdown */}
      {(totalDegraded > 0 || totalError > 0 || totalOffline > 0 || totalIdle > 0 || totalUndetermined > 0) && (
        <div className="flex gap-3 mb-4 flex-wrap">
          {totalError > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>
              <XCircle className="h-4 w-4" />
              <span className="font-semibold">{totalError}</span> tenant{totalError !== 1 ? 's' : ''} in error
            </div>
          )}
          {totalDegraded > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(217,119,6,0.08)', color: '#d97706' }}>
              <AlertTriangle className="h-4 w-4" />
              <span className="font-semibold">{totalDegraded}</span> tenant{totalDegraded !== 1 ? 's' : ''} degraded
            </div>
          )}
          {totalOffline > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(107,114,128,0.08)', color: '#6b7280' }}>
              <WifiOff className="h-4 w-4" />
              <span className="font-semibold">{totalOffline}</span> offline
            </div>
          )}
          {totalIdle > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(37,99,235,0.08)', color: '#2563eb' }}>
              <Pause className="h-4 w-4" />
              <span className="font-semibold">{totalIdle}</span> idle
            </div>
          )}
          {totalUndetermined > 0 && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(156,163,175,0.12)', color: '#6b7280' }}>
              <HelpCircle className="h-4 w-4" />
              <span className="font-semibold">{totalUndetermined}</span> undetermined
            </div>
          )}
        </div>
      )}

      {/* Tenant list */}
      <SectionCard>
        <SectionHeader
          title="Tenant Sync Status"
          icon={Database}
          expanded={expanded.tenants}
          onToggle={() => toggle('tenants')}
          badge={<span className="text-xs opacity-50 mr-2">{filtered.length} of {tenants.length}</span>}
        />
        {expanded.tenants && (
          <>
            {/* Search + filter toolbar */}
            <div className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <Search className="h-3.5 w-3.5 opacity-40" />
                <input
                  type="text"
                  placeholder="Search tenants…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="flex-1 text-sm bg-transparent outline-none placeholder:opacity-40"
                />
              </div>
              <div className="flex gap-1">
                {(['all', 'healthy', 'degraded', 'error', 'offline', 'idle', 'undetermined'] as const).map(s => (
                  <button
                    key={s}
                    onClick={() => setFilterStatus(s)}
                    className="px-2 py-1 rounded text-xs font-medium capitalize transition-colors"
                    style={{
                      background: filterStatus === s ? 'rgba(0,0,0,0.08)' : 'transparent',
                      fontWeight: filterStatus === s ? 600 : 400,
                    }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Column headers */}
            <div className="flex items-center gap-4 px-4 py-2 text-xs opacity-40 font-semibold uppercase tracking-wide border-b"
              style={{ borderColor: 'rgba(0,0,0,0.04)' }}>
              <span className="w-2" />
              <span className="w-48">Tenant</span>
              <span className="w-28">Slug</span>
              <span className="w-20">SSE</span>
              <span className="w-28">Last Sync</span>
              <span className="ml-auto">Queue / Conflicts</span>
              <span className="w-8" />
            </div>

            {/* Rows */}
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm opacity-40">
                  {tenants.length === 0
                    ? 'No tenant data available. Verify the system-api is reachable and has tenants.'
                    : 'No tenants match the current filter.'}
                </p>
              </div>
            ) : (
              filtered
                .slice()
                .sort((a, b) => {
                  // Sort: error, degraded, offline, idle, undetermined, healthy
                  const order = { error: 0, degraded: 1, offline: 2, idle: 3, undetermined: 4, healthy: 5 };
                  return (order[a.healthStatus] ?? 6) - (order[b.healthStatus] ?? 6);
                })
                .map(tenant => <TenantRow key={tenant._id} tenant={tenant} />)
            )}

            {tenants.length === 0 && (
              <div className="px-4 py-3 border-t text-xs opacity-40"
                style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                No tenants returned by <code>/api/sync/tenant-health</code>.
                Verify the system-api is reachable and the system DB <code>tenants</code> collection has records.
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* Legend */}
      <div className="flex items-center gap-6 text-xs opacity-50 mt-2 px-1 flex-wrap">
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-green-600 inline-block" /> Healthy</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500 inline-block" /> Degraded</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-600 inline-block" /> Error</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gray-400 inline-block" /> Offline</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-600 inline-block" /> Idle (connected, nothing to sync)</span>
        <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-gray-300 inline-block" /> Undetermined (no sync signal yet)</span>
        <span className="ml-auto flex items-center gap-1"><Zap className="h-3 w-3" /> = SSE live push · ↑ = pending · ✗ = failed · ⚡ = conflict</span>
      </div>
    </div>
  );
}
